/**
 * PanelFooter — the one "go to the full page" row at the foot of a header
 * panel (a popover or dropdown opened from the app bar).
 *
 * Every header panel that links out uses it, so they read as one family: the
 * citizen bell → Inbox, the journey switcher → Manage journeys, the business
 * bell → all notifications. Each used to draw its own — a centred accent link,
 * a small muted row, a centred "See all" — three answers to one question.
 *
 * ─── The pattern ──────────────────────────────────────────────────────────
 *
 * A hairline divider, then a full-width row: a leading icon naming the
 * destination, a left-aligned label, and a trailing arrow that says "this
 * leaves the panel". Quiet at rest (the panel's content is the point), the
 * ghost button's hover tint on hover, keyboard focus and menu highlight. 40px
 * tall — a comfortable pointer and touch target — with an inset focus ring the
 * panel's rounded, clipped edge cannot cut off.
 *
 * ─── Usage ────────────────────────────────────────────────────────────────
 *
 * Router-agnostic, in the `buttonVariants` idiom: put the class recipe on the
 * app's own link and the content inside it.
 *
 *   <PanelFooter>
 *     <Link href="/inbox" className={panelFooterLinkVariants()}>
 *       <PanelFooterLinkContent icon={Inbox}>{t('inbox.openFromBell')}</PanelFooterLinkContent>
 *     </Link>
 *   </PanelFooter>
 *
 * Inside a Radix DropdownMenu the row must stay a menu item (arrow-key order;
 * the menu traps Tab), so give the recipe to `DropdownMenuItem` — its `cn`
 * resolves the recipe against the item's own padding, gap and radius:
 *
 *   <DropdownMenuItem asChild className={panelFooterLinkVariants()}>
 *     <Link href="/journeys/manage">
 *       <PanelFooterLinkContent icon={Settings}>{label}</PanelFooterLinkContent>
 *     </Link>
 *   </DropdownMenuItem>
 */
import * as React from 'react';
import { ArrowRight } from 'lucide-react';
import { cn } from '@tonnie/foundation';

/** The divider and inset that hold the footer's link. */
export function PanelFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="panel-footer"
      className={cn('border-t border-border/60 p-1.5', className)}
      {...props}
    />
  );
}

/** The footer link's class recipe — for an `<a>`, a router link or a menu item. */
export function panelFooterLinkVariants(className?: string): string {
  return cn(
    'group/panel-link flex h-10 w-full cursor-pointer items-center gap-2.5 rounded-md px-3 py-0',
    'text-sm font-medium text-foreground outline-none transition-colors',
    // `focus:` too: a menu item takes focus on hover, and the item's own
    // `focus:bg-accent` would otherwise tint it unlike the popover rows.
    'hover:bg-muted focus:bg-muted focus:text-foreground data-[highlighted]:bg-muted',
    'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
    className
  );
}

type PanelFooterIcon = React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;

/** Leading icon, label, trailing arrow — the inside of every footer link. */
export function PanelFooterLinkContent({
  icon: Icon,
  children,
}: {
  icon: PanelFooterIcon;
  children: React.ReactNode;
}) {
  return (
    <>
      <Icon
        aria-hidden
        className="size-4 shrink-0 text-muted-foreground transition-colors group-hover/panel-link:text-foreground group-focus-visible/panel-link:text-foreground"
      />
      <span className="min-w-0 flex-1 truncate">{children}</span>
      <ArrowRight
        aria-hidden
        className="size-4 shrink-0 text-muted-foreground transition-transform group-hover/panel-link:translate-x-0.5 group-hover/panel-link:text-foreground motion-reduce:transition-none motion-reduce:group-hover/panel-link:translate-x-0"
      />
    </>
  );
}
